import SupplierBreadcrumbs from '@bridebook/web/app-basic/components/breadcrumbs/breadcrumbs-search-suppliers.container';
import { AutocompleteInputLocation } from '@bridebook/web/app-basic/components/search/location-autocomplete/autocomplete-input-location';
import GooglePredictionsContextProvider from '@bridebook/web/app-basic/components/search/location-autocomplete/context';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import useLoggedOutSearchBar from '@bridebook/web/app-basic/lib/hooks/use-logged-out-search-bar';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { prettySupplierCategory } from '@bridebook/web/app-shared/lib/utils';
import { useTranslation } from 'next-i18next';
import { memo, useEffect } from 'react';
import { LoadingCircle } from '@bridebook/ui';
import { renderHtml } from '@bridebook/web/app-shared/components/articles/render-html';
import SearchSuppliersCategories from '@bridebook/web/app-basic/components/search-suppliers/search-suppliers-categories/search-suppliers-categories';
import SearchSuppliersSignUp from '@bridebook/web/app-basic/components/search-suppliers/search-suppliers-categories/search-suppliers-sign-up';
import SearchSuppliersContainer from '@bridebook/web/app-basic/components/search-suppliers/search-suppliers-container';
import { useSearchSuppliersContext } from '@bridebook/web/app-basic/components/search-suppliers/search-suppliers.context';
import Heading from '@bridebook/web/app-shared/components/suppliers-categories/heading';
import InfoParagraph from '@bridebook/web/app-shared/components/suppliers-categories/info-paragraph';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { Flex, Icon } from '@bridebook/aura';
import { Search } from '@bridebook/aura/src/icons';
import SearchSuppliersApp from './search-suppliers-categories/search-suppliers-app';

const INPUT_HEIGHT = 48;

const SearchSuppliers = () => {
  const market = useMarket();
  const { slug, specific, typeSpecific } = useSearchSuppliersContext();
  const { t } = useTranslation('loggedOutSearchBar');
  const isMobile = useSelector(getIsMobile);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const { onPlaceSelected, handleOnFocus, searchBarEnabled, loading, handleOnBlur } =
    useLoggedOutSearchBar('mainSearchPageSearchBar', true, slug);

  const supplierName = prettySupplierCategory({
    slug,
    amount: 2,
    isLoggedIn: false,
    market,
  });

  const subheading = typeSpecific?.subheading
    ? renderHtml({ htmlContent: typeSpecific.subheading, market })
    : specific.subheading;
  const description = typeSpecific?.description
    ? renderHtml({ htmlContent: typeSpecific.description, market })
    : specific.description;

  return (
    <div data-name="search-suppliers">
      <SearchSuppliersContainer>
        {!isMobile && <SupplierBreadcrumbs slug={slug} />}
        <Heading className="lg:!mb-2">{typeSpecific?.h1 || specific.h1}</Heading>
        <div>
          {isMobile && searchBarEnabled && (
            <>
              <InfoParagraph>{subheading}</InfoParagraph>
              <GooglePredictionsContextProvider>
                <AutocompleteInputLocation
                  name="address-auto-suggest"
                  onPlaceChanged={onPlaceSelected}
                  label={t('autocomplete.modalLabel')}
                  placeholder={t('autocomplete.placeholder.supplier', { supplier: supplierName })}
                  disabled={loading}
                  style={{ height: INPUT_HEIGHT }}
                  beforeIcon={
                    <Flex className="mr-1 h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-crush">
                      <Icon icon={Search} size={16} className="text-white" />
                    </Flex>
                  }
                  afterInput={
                    loading ? (
                      <Flex className="mr-2 h-8 min-w-8 items-center justify-center rounded-full bg-space-40">
                        <LoadingCircle size={18} color="white" center />
                      </Flex>
                    ) : undefined
                  }
                />
              </GooglePredictionsContextProvider>
            </>
          )}
          {!isMobile && searchBarEnabled && (
            <>
              <InfoParagraph>{subheading}</InfoParagraph>
              <GooglePredictionsContextProvider>
                <div className="-ml-4 max-w-[362px]">
                  <AutocompleteInputLocation
                    name="address-auto-suggest"
                    onPlaceChanged={onPlaceSelected}
                    placeholder={t('autocomplete.placeholder.supplier', { supplier: supplierName })}
                    onFocusProp={handleOnFocus}
                    onBlurProp={handleOnBlur}
                    disabled={loading}
                    style={{ height: INPUT_HEIGHT }}
                    beforeIcon={
                      <Flex className="mr-1 h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-crush">
                        <Icon icon={Search} size={16} className="text-white" />
                      </Flex>
                    }
                    afterInput={
                      loading ? (
                        <Flex className="mr-2 h-8 w-8 min-w-8 items-center justify-center rounded-[50%] bg-space-40">
                          <LoadingCircle size={24} color="white" center />
                        </Flex>
                      ) : undefined
                    }
                  />
                </div>
              </GooglePredictionsContextProvider>
            </>
          )}
        </div>
      </SearchSuppliersContainer>
      <SearchSuppliersCategories market={market}>
        <SearchSuppliersSignUp />
      </SearchSuppliersCategories>
      <SearchSuppliersContainer>
        <>
          <InfoParagraph
            className={!isMobile ? 'my-16 rounded-3xl border border-space-15 p-20' : undefined}>
            {description}
          </InfoParagraph>
          <SearchSuppliersApp />
        </>
      </SearchSuppliersContainer>
    </div>
  );
};

export default memo(SearchSuppliers);
